import React, { useCallback } from 'react';
import Fullscreen from '@uber/base-icons/fullscreen';
import ZoomIn from '@uber/base-icons/zoom-in';
import ZoomOut from '@uber/base-icons/zoom-out';
import { type Theme, useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { CANVAS_TOOLBAR } from '../../../constants/template-builder';
import { useCoreStageContext } from '../../../providers/core-stage-provider/context';
import { UndoRedoButtons } from './undo-redo-buttons';
type CanvasToolbarPropsT = {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};
const ACTION_BUTTONS = ([{
  id: 'zoom-in',
  tooltip: CANVAS_TOOLBAR.ZOOM_IN,
  Icon: ZoomIn,
  action: 'zoomIn'
}, {
  id: 'zoom-out',
  tooltip: CANVAS_TOOLBAR.ZOOM_OUT,
  Icon: ZoomOut,
  action: 'zoomOut'
}, {
  id: 'fit-to-screen',
  tooltip: CANVAS_TOOLBAR.FIT_TO_SCREEN,
  Icon: Fullscreen,
  action: 'fit'
}] as const);
const getContainerStyles = (theme: Theme) => ({
  position: ('absolute' as const),
  bottom: theme.sizing.scale600,
  left: theme.sizing.scale600,
  display: ('flex' as const),
  flexDirection: ('column' as const),
  alignItems: ('center' as const),
  padding: theme.sizing.scale300,
  gap: theme.sizing.scale400,
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: theme.borders.radius300,
  boxShadow: theme.lighting.shadow400,
  boxSizing: ('border-box' as const),
  pointerEvents: ('auto' as const)
});
const getDividerStyles = (theme: Theme) => ({
  width: '100%',
  height: theme.sizing.scale0,
  backgroundColor: theme.colors.borderOpaque
});
export const BUTTON_OVERRIDES = {
  BaseButton: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      width: $theme.sizing.scale900,
      height: $theme.sizing.scale900,
      minWidth: $theme.sizing.scale900,
      display: 'flex',
      alignItems: 'center',
      padding: '0',
      justifyContent: 'center'
    })
  }
};
export function CanvasToolbar({
  onUndo,
  onRedo,
  canUndo,
  canRedo
}: CanvasToolbarPropsT) {
  const [css, theme] = useStyletron();
  const {
    getStageSettings,
    setStageSettings,
    fitToScreen
  } = useCoreStageContext();
  const handleActionClick = useCallback((action: string) => {
    const {
      scale
    } = getStageSettings();
    switch (action) {
      case 'zoomIn':
        setStageSettings({
          scale: scale * CANVAS_TOOLBAR.ZOOM_IN_FACTOR
        });
        break;
      case 'zoomOut':
        setStageSettings({
          scale: scale * CANVAS_TOOLBAR.ZOOM_OUT_FACTOR
        });
        break;
      case 'fit':
        fitToScreen();
        break;
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  }, [getStageSettings, setStageSettings, fitToScreen]);
  return <div className={css(getContainerStyles(theme))} data-testid="canvas-toolbar">
      {ACTION_BUTTONS.map(({
      id,
      tooltip,
      Icon,
      action
    }) => <StatefulTooltip key={id} content={tooltip} placement={PLACEMENT.right} showArrow>
          <Button data-testid={`toolbar-${id}`} onClick={() => handleActionClick(action)} kind={KIND.tertiary} size={SIZE.xSmall} overrides={BUTTON_OVERRIDES}>
            <Icon size={CANVAS_TOOLBAR.ICON_SIZE} />
          </Button>
        </StatefulTooltip>)}

      <div className={css(getDividerStyles(theme))} />

      <UndoRedoButtons onUndo={onUndo} onRedo={onRedo} canUndo={canUndo} canRedo={canRedo} overrides={BUTTON_OVERRIDES} />
    </div>;
}